// ============================================================================
// Banker Conversations — the human gateway over Kavya/Drishti/Raja's inbound
// email triage. Tier-1 rule: nothing is sent until a human clicks "Send".
//
// Two columns:
//   Left: investor list (one row per investor with inbound + cold "Unmatched"
//         bucket pinned at the top).
//   Right: interleaved outbound + inbound thread for the selected investor.
//          Each pending inbound shows the LLM's intent + suggested reply in
//          an editable textarea + "Send as <persona>" button + Take over /
//          Archive.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Inbox, Loader2, MailQuestion, Send, ShieldAlert, ShieldCheck, UserMinus, ArrowLeft, Sparkles, AlertTriangle } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

const INTENT_COLORS: Record<string, string> = {
  accept_call: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  counter_propose: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  decline_call: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  question: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  not_interested: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  thanks: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  other: "bg-white/[0.06] text-white/60 border-white/15",
};

function relTime(d: string | Date) {
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return new Date(d).toLocaleDateString();
}

export default function BankerConversations() {
  const [, navigate] = useLocation();
  const [list, setList] = useState<{ investors: any[]; unmatched: any[] } | null>(null);
  const [activeInvestorId, setActiveInvestorId] = useState<number | null>(null);
  const [thread, setThread] = useState<any | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("banker_token") || "" : "";
  const headers = { "Content-Type": "application/json", "x-banker-token": token };

  const loadList = async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${BASE}api/banker/me/conversations`, { headers });
      if (r.ok) setList(await r.json());
    } catch {}
    setLoadingList(false);
  };

  const loadThread = async (investorId: number) => {
    setLoadingThread(true);
    try {
      const r = await fetch(`${BASE}api/banker/me/conversations/${investorId}`, { headers });
      if (r.ok) {
        const t = await r.json();
        setThread(t);
        // Pre-fill drafts from suggested replies.
        const d: Record<number, string> = {};
        for (const m of t.messages || []) {
          if (m.kind === "in" && m.triageSuggestedReply && (m.triageStatus === "pending" || m.triageStatus === "classified")) {
            d[m.inboundId] = m.triageSuggestedReply;
          }
        }
        setDrafts(d);
      }
    } catch {}
    setLoadingThread(false);
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeInvestorId) loadThread(activeInvestorId); }, [activeInvestorId]);

  const sendReply = async (inboundId: number) => {
    const body = (drafts[inboundId] || "").trim();
    if (!body) return;
    setSendingId(inboundId);
    try {
      const r = await fetch(`${BASE}api/banker/me/conversations/${inboundId}/send`, {
        method: "POST", headers, body: JSON.stringify({ body }),
      });
      if (r.ok) {
        if (activeInvestorId) await loadThread(activeInvestorId);
        await loadList();
      } else {
        alert(`Send failed: ${(await r.json().catch(() => ({})))?.detail || r.statusText}`);
      }
    } catch (err: any) {
      alert(`Send failed: ${err?.message || "unknown"}`);
    }
    setSendingId(null);
  };

  const action = async (inboundId: number, kind: "take-over" | "archive") => {
    setBusyId(inboundId);
    try {
      await fetch(`${BASE}api/banker/me/conversations/${inboundId}/${kind}`, { method: "POST", headers });
      if (activeInvestorId) await loadThread(activeInvestorId);
      await loadList();
    } catch {}
    setBusyId(null);
  };

  const totalUnread = useMemo(() => (list?.investors || []).reduce((s, i) => s + (i.unread || 0), 0) + (list?.unmatched?.length || 0), [list]);

  return (
    <div className="h-[calc(100vh-7rem)] md:h-[calc(100vh-6rem)] -mx-2 md:mx-0 flex flex-col">
      <div className="flex items-center justify-between gap-3 px-3 md:px-1 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Inbox className="w-6 h-6 text-[#FF5C00]" />
          <h1 className="text-2xl md:text-3xl font-bold text-white">Conversations</h1>
          {totalUnread > 0 && <span className="bg-[#FF5C00] text-white text-[11px] font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1.5">{totalUnread}</span>}
        </div>
        <p className="hidden md:block text-[11px] text-white/40">Investor replies to Kavya, Drishti, Raja, Riya. You decide what gets sent.</p>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_1fr] gap-3 px-1">
        {/* List */}
        <div className="flex flex-col min-h-0 bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loadingList ? (
              <div className="p-3 space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
            ) : (
              <>
                {/* Unmatched bucket pinned on top */}
                {list && list.unmatched.length > 0 && (
                  <div className="border-b border-white/[0.08]">
                    <div className="px-3 py-2 bg-amber-500/5 border-b border-amber-500/15 flex items-center gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-300" />
                      <p className="text-[11px] uppercase tracking-wider text-amber-200/85 font-mono">Unmatched ({list.unmatched.length})</p>
                    </div>
                    {list.unmatched.map(u => (
                      <div key={u.id} className="px-3 py-3 border-b border-white/[0.04] hover:bg-white/[0.04]">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm text-white/90 truncate font-medium">{u.fromEmail}</p>
                          <span className="text-[10px] text-white/40 font-mono">{relTime(u.receivedAt)}</span>
                        </div>
                        <p className="text-xs text-white/65 truncate mt-0.5">→ {u.toPersona}@favcyselect.com</p>
                        <p className="text-xs text-white/55 truncate mt-0.5">{u.subject || "(no subject)"}</p>
                        {u.triageIntent && (
                          <span className={`inline-block mt-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${INTENT_COLORS[u.triageIntent] || INTENT_COLORS.other}`}>
                            {u.triageIntent.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Investor list */}
                {list && list.investors.length === 0 && list.unmatched.length === 0 ? (
                  <div className="p-12 text-center">
                    <Inbox className="w-10 h-10 text-white/15 mx-auto mb-3" />
                    <p className="text-white/45 text-sm">No conversations yet.</p>
                    <p className="text-white/30 text-xs mt-1">Investors haven't replied to anything from your team.</p>
                  </div>
                ) : (
                  list?.investors.map(row => {
                    const active = activeInvestorId === row.investor.id;
                    return (
                      <button key={row.investor.id} onClick={() => setActiveInvestorId(row.investor.id)}
                        className={`w-full text-left px-3 py-3 border-b border-white/[0.04] flex items-start gap-2.5 transition-colors ${active ? "bg-[#FF5C00]/[0.08]" : "hover:bg-white/[0.04]"}`}>
                        <div className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/[0.1] flex items-center justify-center font-semibold text-xs text-white/85 flex-shrink-0">
                          {(row.investor.name || row.investor.email || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className={`text-sm truncate ${row.unread > 0 ? "text-white font-semibold" : "text-white/75"}`}>{row.investor.name || row.investor.email}</p>
                            <span className="text-[10px] text-white/40 font-mono flex-shrink-0">{relTime(row.lastAt)}</span>
                          </div>
                          <p className="text-xs text-white/55 truncate mt-0.5">{row.investor.email}</p>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <span className="text-[10px] text-white/45 font-mono">{row.total} msg{row.total === 1 ? "" : "s"}</span>
                            {row.unread > 0 && <span className="bg-[#FF5C00] text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">{row.unread}</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="flex flex-col min-h-0 bg-white/[0.03] border border-white/[0.08] rounded-2xl overflow-hidden">
          {!activeInvestorId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MailQuestion className="w-12 h-12 text-white/15 mb-3" />
              <p className="text-white/55 text-sm">Pick an investor on the left.</p>
              <p className="text-white/35 text-xs mt-2 max-w-sm">For each inbound reply, AI suggests a draft. You edit, then click <span className="text-[#FF5C00] font-medium">Send as Kavya</span> (or whoever started the thread).</p>
            </div>
          ) : loadingThread || !thread ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 text-white/40 animate-spin" /></div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-white/[0.08] flex items-center gap-3">
                <button onClick={() => { setActiveInvestorId(null); setThread(null); }} className="md:hidden text-white/60 hover:text-white p-1 -ml-1"><ArrowLeft className="w-4 h-4" /></button>
                <div className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.1] flex items-center justify-center font-semibold text-sm text-white/85">
                  {(thread.investor.name || thread.investor.email || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-base truncate">{thread.investor.name}</p>
                  <p className="text-white/55 text-xs mt-0.5">{thread.investor.email}</p>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
                {(thread.messages || []).length === 0 && (
                  <p className="text-white/40 text-sm text-center py-8">No messages with this investor yet.</p>
                )}
                {(thread.messages || []).map((m: any) => {
                  if (m.kind === "out") {
                    return (
                      <div key={m.id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-pink-500/15 border border-pink-500/30 flex items-center justify-center text-pink-300 font-semibold text-[11px] flex-shrink-0">
                          {(m.persona || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[11px] text-white/55 font-medium capitalize">{m.persona} → {m.toEmail}</span>
                            <span className="text-[10px] text-white/30 font-mono">{relTime(m.at)}</span>
                          </div>
                          <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] px-3.5 py-2.5">
                            <p className="text-white text-sm font-medium mb-1">{m.subject}</p>
                            <p className="text-white/65 text-xs whitespace-pre-wrap leading-relaxed">{m.bodySnippet || "(no snippet)"}</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  // inbound
                  const intentColor = INTENT_COLORS[m.triageIntent || "other"] || INTENT_COLORS.other;
                  const editable = m.triageStatus === "pending" || m.triageStatus === "classified";
                  return (
                    <div key={m.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-300 font-semibold text-[11px] flex-shrink-0">
                        {(thread.investor.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[11px] text-white/55 font-medium">{thread.investor.name} → {m.toPersona}</span>
                          <span className="text-[10px] text-white/30 font-mono">{relTime(m.at)}</span>
                          {m.triageIntent && (
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${intentColor}`}>
                              {m.triageIntent.replace(/_/g, " ")}
                              {typeof m.triageConfidence === "number" && <span className="opacity-70 ml-1">{Math.round(m.triageConfidence * 100)}%</span>}
                            </span>
                          )}
                          {m.triageStatus === "replied" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">replied</span>}
                          {m.triageStatus === "archived" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-zinc-500/15 text-zinc-300 border-zinc-500/30">archived</span>}
                          {m.triageStatus === "pending" && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30">awaiting triage</span>}
                        </div>
                        <div className="rounded-xl bg-sky-500/[0.06] border border-sky-500/[0.18] px-3.5 py-2.5">
                          <p className="text-white text-sm font-medium mb-1">{m.subject}</p>
                          <p className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{m.bodyText || (m.bodyHtml ? "(HTML body — see suggested reply below)" : "(empty)")}</p>
                          {m.triageNotes && (
                            <p className="text-[11px] text-white/50 mt-2 italic flex items-start gap-1.5">
                              <Sparkles className="w-3 h-3 text-[#FF5C00] mt-0.5 flex-shrink-0" />
                              <span>AI note: {m.triageNotes}</span>
                            </p>
                          )}
                        </div>

                        {editable && (
                          <div className="mt-2 rounded-xl bg-white/[0.04] border border-white/[0.08] p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />
                              <p className="text-[11px] text-white/60 font-medium">Suggested reply (edit before sending)</p>
                            </div>
                            <textarea
                              value={drafts[m.inboundId] ?? ""}
                              onChange={(e) => setDrafts(d => ({ ...d, [m.inboundId]: e.target.value }))}
                              rows={5}
                              placeholder={m.triageSuggestedReply ? "" : "AI hasn't drafted a reply yet — type your own."}
                              className="w-full bg-transparent text-sm text-white placeholder-white/35 outline-none resize-none p-1"
                            />
                            <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                              <p className="text-[10px] text-white/40">Sends from <span className="text-white/65">{m.toPersona}@favcyselect.com</span> · Reply-To you</p>
                              <div className="flex items-center gap-1.5">
                                <button onClick={() => action(m.inboundId, "take-over")} disabled={busyId === m.inboundId}
                                  className="text-[11px] text-white/65 hover:text-white px-2.5 py-1.5 rounded-lg border border-white/[0.1] hover:border-white/[0.2] disabled:opacity-40 flex items-center gap-1.5">
                                  <UserMinus className="w-3 h-3" /> Take over
                                </button>
                                <button onClick={() => action(m.inboundId, "archive")} disabled={busyId === m.inboundId}
                                  className="text-[11px] text-white/65 hover:text-white px-2.5 py-1.5 rounded-lg border border-white/[0.1] hover:border-white/[0.2] disabled:opacity-40">
                                  Archive
                                </button>
                                <button onClick={() => sendReply(m.inboundId)} disabled={sendingId === m.inboundId || !(drafts[m.inboundId] || "").trim()}
                                  className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 text-white px-3 py-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1.5">
                                  {sendingId === m.inboundId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                  Send as {m.toPersona}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
